// Updated snapshot.js - Add functions to check voting status and power

import { ethers } from "ethers";
import snapshot from "@snapshot-labs/snapshot.js";
import { ENV } from "./config";

// Setup Snapshot client and config
const client = new snapshot.Client712(ENV.snapshotUrl);
const spaceName = ENV.spaceName;
const rpcProvider = new ethers.providers.JsonRpcProvider(ENV.rpcProvider);

// Check if user has already voted on a proposal
const checkUserVoteStatus = async (proposalId, userAddress) => {
  if (!userAddress) return { hasVoted: false, choice: null };
  
  try {
    const query = {
      query: `{
        votes(
          where: { 
            proposal: "${proposalId}",
            voter: "${userAddress.toLowerCase()}"
          }
        ) {
          id
          voter
          choice
          vp
        }
      }`
    };
    
    const res = await fetch(`${ENV.snapshotUrl}/graphql`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(query),
    });
    
    const data = await res.json();
    const votes = data.data.votes;
    
    if (votes && votes.length > 0) {
      return {
        hasVoted: true,
        choice: votes[0].choice,
        votingPower: votes[0].vp
      };
    }
    
    return { hasVoted: false, choice: null };
  } catch (error) {
    console.error("Error checking vote status:", error);
    return { hasVoted: false, choice: null };
  }
};

// Check user's voting power for a proposal - checks LITHO token balance
const checkVotingPower = async (proposalId, userAddress, snapshotBlock) => {
  if (!userAddress) return { hasVotingPower: false, votingPower: 0 };
  
  try {
    const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
    
    // ERC20 token ABI - only need balanceOf function
    const erc20Abi = [
      "function balanceOf(address owner) view returns (uint256)",
      "function decimals() view returns (uint8)"
    ];
    
    // Create contract instance for LITHO token
    const lithoTokenAddress = ENV.contractAddress.lithoToken;
    const tokenContract = new ethers.Contract(lithoTokenAddress, erc20Abi, web3Provider);
    
    // Get token balance
    const balance = await tokenContract.balanceOf(userAddress);
    
    // Get token decimals (usually 18 for LITHO)
    const decimals = await tokenContract.decimals();
    
    // Convert balance to readable format
    const balanceFormatted = ethers.utils.formatUnits(balance, decimals);
    const balanceNumber = parseFloat(balanceFormatted);
    
    // User has voting power if they have any LITHO tokens
    const hasVotingPower = balanceNumber > 0;
    
    console.log(`Voting power check - Address: ${userAddress}, LITHO Balance: ${balanceNumber}`);
    
    return {
      hasVotingPower: hasVotingPower,
      votingPower: balanceNumber
    };
  } catch (error) {
    console.error("Error checking voting power:", error);
    return { hasVotingPower: false, votingPower: 0 };
  }
};

// Updated castVote function with better error handling
const castVote = async (proposalId, selectedChoiceIndex, userAddress) => {
  try {
    // Check if user has already voted
    const voteStatus = await checkUserVoteStatus(proposalId, userAddress);
    if (voteStatus.hasVoted) {
      throw new Error("You have already voted on this proposal");
    }

    // Check voting power
    const powerCheck = await checkVotingPower(proposalId, userAddress);
    if (!powerCheck.hasVotingPower) {
      throw new Error("Insufficient voting power to participate in this proposal");
    }

    const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
    await web3Provider.send("eth_requestAccounts", []);
    const signer = web3Provider.getSigner();
    const account = await signer.getAddress();
    
    const voteReceipt = await client.vote(signer, account, {
      space: spaceName,
      proposal: proposalId,
      type: 'single-choice',
      choice: selectedChoiceIndex,
      app: ENV.appName
    });
    
    return { success: true, receipt: voteReceipt };
  } catch (error) {
    console.error("Voting error:", error);
    return { success: false, error: error.message };
  }
};

// Fixed createProposal - uses erc20-balance-of strategy directly for LITHO tokens
const createProposal = async (title, description) => {
  const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
  await web3Provider.send("eth_requestAccounts", []);
  const signer = web3Provider.getSigner();
  const account = await signer.getAddress();
  
  const snapshotBlock = await rpcProvider.getBlockNumber();
  console.log("snapshot block: ", snapshotBlock);
  
  try {
    // Define erc20-balance-of strategy directly for LITHO tokens
    const strategies = [
      {
        name: "erc20-balance-of",
        network: String(ENV.requiredChainIds.bsc), // BSC mainnet = 56
        params: {
          address: ENV.contractAddress.lithoToken, // LITHO token address
          symbol: "LITHO",
          decimals: 18
        }
      }
    ];
    
    const proposalData = {
      space: spaceName,
      type: ENV.voteType,
      title,
      body: description,
      choices: ENV.choices,
      start: Math.floor(Date.now() / 1000) + ENV.votingStartIn,
      end: Math.floor(Date.now() / 1000) + ENV.votingEndIn,
      snapshot: snapshotBlock,
      network: String(ENV.requiredChainIds.bsc),
      // Use erc20-balance-of strategy directly - calculates voting power from LITHO token balance
      strategies: strategies,
      // Plugins as JSON string
      plugins: JSON.stringify({}),
      app: ENV.appName,
    };

    console.log("Creating proposal with strategies:", strategies);
    
    const receipt = await client.proposal(signer, account, proposalData);
    
    if (!receipt) {
      throw new Error("Proposal creation failed: No receipt returned");
    }
    
    console.log("Proposal created successfully:", receipt);
    return receipt;
  } catch (error) {
    console.error("Error creating proposal:", error);
    
    // Extract meaningful error message
    let errorMessage = "Failed to create proposal";
    if (error?.message) {
      errorMessage = error.message;
    } else if (error?.error_description) {
      errorMessage = error.error_description;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }
    
    throw new Error(errorMessage);
  }
};

const fetchProposals = async (page = 1, limit = 20) => {
  const skip = (page - 1) * limit;
  
  const query = {
    query: `{
      proposals(
        where: { space: "${spaceName}" }
        first: ${limit}
        skip: ${skip}
        orderBy: "created"
        orderDirection: desc
      ) { 
        id
        title
        body
        choices
        scores
        state
        start
        end
        created
      }
    }`
  };
  
  const res = await fetch(`${ENV.snapshotUrl}/graphql`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(query),
  });
  
  const data = await res.json();
  const proposals = data.data.proposals;
  
  return {
    proposals,
    pagination: {
      page,
      limit,
      hasMore: proposals.length === limit
    }
  };
};

const fetchProposalDetails = async (id) => {
  const query = {
    query: `{
      proposals(where: { space: "${spaceName}", id: "${id}" }) {
        id
        title
        body
        choices
        scores
        state
        start
        end
        snapshot
      }
    }`
  };
  
  const res = await fetch(`${ENV.snapshotUrl}/graphql`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(query),
  });
  
  const data = await res.json();
  const proposal = data.data.proposals;
  return proposal;
};

const evaluateProposalResult = (proposal, quorum = 1000, vetoThreshold = 0.33) => {
  const { scores = [], choices = [], state } = proposal;
  if (state !== "closed") return "Voting in progress";

  const totalVotes = scores.reduce((sum, val) => sum + val, 0);
  if (totalVotes < quorum) return "Rejected (Quorum not met)";

  const approveIndex = choices.findIndex(c => c.toLowerCase() === "approve");
  const rejectIndex = choices.findIndex(c => c.toLowerCase() === "reject");
  const vetoIndex = choices.findIndex(c => c.toLowerCase().includes("veto"));

  const approveVotes = scores[approveIndex] || 0;
  const rejectVotes = scores[rejectIndex] || 0;
  const vetoVotes = vetoIndex !== -1 ? scores[vetoIndex] || 0 : 0;

  if (vetoVotes / totalVotes > vetoThreshold) return "Rejected (Veto)";

  return approveVotes > rejectVotes ? "Approved" : "Rejected";
};

const encodePath = () => {
  const dst = ENV.contractAddress.polygonGovernance;
  const src = ENV.contractAddress.bscGovernance;

  const packed = ethers.utils.solidityPack(["address", "address"], [dst, src]);
  console.log("For BSC Mainnet", packed);

  const dst1 = ENV.contractAddress.bscGovernance;
  const src1 = ENV.contractAddress.polygonGovernance;

  const packed1 = ethers.utils.solidityPack(["address", "address"], [dst1, src1]);
  console.log("For Polygon Mainnet", packed1);
};

export {
  createProposal,
  castVote,
  fetchProposals,
  fetchProposalDetails,
  evaluateProposalResult,
  checkUserVoteStatus,
  checkVotingPower,
  encodePath
};